import React, { useState, useMemo, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { orderBy } from 'lodash';

import CircularProgress from '@mui/material/CircularProgress';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';

import NoteListItem from './NoteListItem';
import AlertModal from '../AlertModal';
import Button from '../../shared/Button';
import Input from '../../shared/Input';

import { createNote, updateCaseNote } from '../../../actions/casesActions';

import { toLocalMoment } from '../../../util/dates';

import { roleNames } from '../../../constants/userRoles';

const { GLOBAL_ADMIN, DISTRIBUTOR_ADMIN, ENGINEER } = roleNames;
const internalCaseNoteRoles = [GLOBAL_ADMIN, DISTRIBUTOR_ADMIN, ENGINEER];

const CaseNotes = ({
  activeCase,
  users = [],
  createNote,
  updateCaseNote,
  userRole,
  currentUserId,
  notesLoading,
}) => {
  const [newNote, setNewNote] = useState('');
  const [loading, setLoading] = useState(false);
  const [isExternal, setIsExternal] = useState(false);
  const [isOpen, toggleModal] = useState(false);
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editingNoteText, setEditingNoteText] = useState('');
  const [savingNoteId, setSavingNoteId] = useState(null);

  const containerRef = useRef(null);

  const notes = useMemo(() => {
    const caseNotes = activeCase?.caseNotes && activeCase?.caseNotes?.length ?
      activeCase?.caseNotes?.filter((note) => internalCaseNoteRoles.includes(userRole) ? true : !!note.external) : [];
    const formattedNotes = caseNotes.map((note) => ({ ...note, date: toLocalMoment(note.date) }));
    if (activeCase?.caseNote) {
      return [...formattedNotes, {
        id: `${activeCase.id}_case_note`,
        text: activeCase.caseNote,
        userId: activeCase.userId,
        date: toLocalMoment(activeCase?.completionDates?.step0),
        source: 'case',
      }];
    }
    return formattedNotes;
  }, [activeCase, userRole]);

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [notes.length]);

  const onSubmitClick = async () => {
    if (isExternal) {
      toggleModal(true);
    } else {
      await onSubmit();
    }
  };

  const onSubmit = async () => {
    const nextNote = newNote.trim();

    if (!nextNote.length) {
      return;
    }

    try {
      toggleModal(false);
      setLoading(true);
      setNewNote('');
      await createNote(activeCase, {
        text: nextNote,
        external: internalCaseNoteRoles.includes(userRole) ? isExternal : true,
      });
      setIsExternal(false);
      // setNewNote('');
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const onKeyDown = async (e) => {
    if (e.keyCode === 13) {
      await onSubmit();
    }
  };

  const onEditStart = (note) => {
    setEditingNoteId(note.id);
    setEditingNoteText(note.text || '');
  };

  const onEditCancel = () => {
    setEditingNoteId(null);
    setEditingNoteText('');
  };

  const onEditSave = async (note) => {
    try {
      setSavingNoteId(note.id);
      const updated = await updateCaseNote(activeCase, note, editingNoteText);

      if (updated) {
        onEditCancel();
      }
    } catch (error) {
      console.error(error);
    } finally {
      setSavingNoteId(null);
    }
  };

  if (notesLoading) {
    return (
      <div className="case-notes-container">
        <div className="loading-container">
          <CircularProgress size={32} />
        </div>
      </div>
    );
  }

  return (
    <div className="case-notes-container">
      {notes && notes.length ? (
        <div className="case-notes-list" ref={containerRef}>
          {orderBy(notes, 'date', 'asc').map((item) => (
            <NoteListItem
              key={item.id}
              note={item}
              users={users}
              procedureFlow={activeCase.procedureFlow}
              canEdit={item.userId === currentUserId}
              isEditing={editingNoteId === item.id}
              editingText={editingNoteText}
              onEditTextChange={setEditingNoteText}
              onEditStart={() => onEditStart(item)}
              onEditCancel={onEditCancel}
              onEditSave={() => onEditSave(item)}
              saving={savingNoteId === item.id}
            />
          ))}
        </div>
      ) : (
        <div className="empty-list" />
      )}
      <div className="case-notes_new-note">
        <Input
          id="new-note"
          type="text"
          multiline
          placeholder="Type any case related notes here"
          value={newNote}
          onChange={(e) => setNewNote(e.target.value)}
          variant="outlined"
          onKeyDown={onKeyDown}
        />
        <div className="case-notes_new-note-action">
          {internalCaseNoteRoles.includes(userRole) ? (
            <FormControlLabel
              checked={isExternal}
              control={<Switch color="primary" />}
              label="Activate to send this note to all users"
              labelPlacement="start"
              style={{ marginLeft: 0 }}
              onChange={(e) => setIsExternal(e.target?.checked)}
            />
          ) : (
            <div />
          )}

          <Button
            text="Add new note"
            type="submit"
            color="primary"
            onClick={onSubmitClick}
            disabled={!newNote.trim().length || loading}
            loading={loading}
          />
        </div>
      </div>

      <AlertModal
        open={isOpen}
        onClose={() => toggleModal(false)}
        onSubmit={onSubmit}
        title="Confirm Visibility"
        text="This note will be visible to all case related users"
        submitText="Continue"
      />
    </div>
  );
};

CaseNotes.propTypes = {
  activeCase: PropTypes.object,
};

const mapStateToProps = (state) => {
  return {
    activeCase: state.cases.activeCase,
    users: state.users?.list || [],
    userRole: state.user.currentUser.role,
    currentUserId: state.user.currentUser.uid,
    notesLoading: !!state.cases.activeCase?.caseNotesLoading,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    createNote: (caseId, note) => dispatch(createNote(caseId, note)),
    updateCaseNote: (activeCase, note, text) => dispatch(updateCaseNote(activeCase, note, text)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(CaseNotes);
